import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Save, Receipt } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RegisterDeposit() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [boletaFile, setBoletaFile] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);

  const [formData, setFormData] = useState({
    numero_boleta: '',
    fecha: '',
    banco: '',
    sucursal: '',
    monto: '',
    tienda_id: ''
  });

  useEffect(() => {
    fetchStores();
    fetchBancos();
  }, []);

  const fetchStores = async () => {
    try {
      const response = await axios.get(`${API}/stores`);
      setStores(response.data);
    } catch (error) {
      console.error('Error al cargar tiendas:', error);
    }
  };

  const fetchBancos = async () => {
    try {
      const response = await axios.get(`${API}/bancos`);
      setBancos(response.data);
    } catch (error) {
      console.error('Error al cargar bancos:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const checkDuplicateBoleta = async () => {
    const { numero_boleta, banco, fecha } = formData;
    
    if (!numero_boleta || !banco || !fecha) {
      return;
    }

    setCheckingDuplicate(true);
    try {
      const response = await axios.get(
        `${API}/boletas/check-duplicate/${numero_boleta}?banco=${banco}&fecha=${fecha}`
      );
      setDuplicateWarning(response.data);
    } catch (error) {
      console.error('Error checking duplicate:', error);
    } finally {
      setCheckingDuplicate(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!boletaFile) {
      toast.error('Por favor selecciona la imagen de la boleta');
      return;
    }

    // Check for high confidence duplicates
    if (duplicateWarning?.is_duplicate && duplicateWarning?.confidence === 'high') {
      toast.error('Esta boleta ya está registrada. Por favor verifica los datos.');
      return;
    }

    setLoading(true);

    try {
      // Upload boleta
      const boletaFormData = new FormData();
      boletaFormData.append('numero_boleta', formData.numero_boleta);
      boletaFormData.append('fecha', formData.fecha);
      boletaFormData.append('banco', formData.banco);
      boletaFormData.append('sucursal', formData.sucursal || '');
      boletaFormData.append('monto', formData.monto);
      boletaFormData.append('file', boletaFile);
      if (formData.tienda_id) {
        boletaFormData.append('tienda_id', formData.tienda_id);
      }
      // Note: payment_id is NOT included - this is a standalone deposit

      await axios.post(`${API}/boletas/upload`, boletaFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Depósito registrado exitosamente');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error al registrar depósito:', error);
      toast.error(error.response?.data?.detail || 'Error al registrar el depósito');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/40">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver al Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-slate-900">Registrar Depósito Bancario</h1>
          <p className="text-slate-600 mt-2">Registra una boleta de depósito para su verificación</p>
        </div>

        <Card className="p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex items-center space-x-3 mb-6 pb-4 border-b border-slate-200">
              <div className="bg-blue-100 rounded-full p-3">
                <Receipt className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Información de la Boleta</h2>
                <p className="text-sm text-slate-600">Completa todos los campos del depósito bancario</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="numero_boleta">Número de Boleta *</Label>
                <Input
                  id="numero_boleta"
                  name="numero_boleta"
                  value={formData.numero_boleta}
                  onChange={handleChange}
                  onBlur={checkDuplicateBoleta}
                  required
                  placeholder="Ej: 123456789"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="fecha">Fecha de Depósito *</Label>
                <Input
                  id="fecha"
                  name="fecha"
                  type="date"
                  value={formData.fecha}
                  onChange={handleChange}
                  onBlur={checkDuplicateBoleta}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="banco">Banco *</Label>
                <Select 
                  value={formData.banco} 
                  onValueChange={(value) => {
                    setFormData(prev => ({...prev, banco: value}));
                    // Trigger duplicate check after banco changes
                    setTimeout(checkDuplicateBoleta, 100);
                  }}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar banco" />
                  </SelectTrigger>
                  <SelectContent>
                    {bancos.map(banco => (
                      <SelectItem key={banco.id} value={banco.nombre}>
                        {banco.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="sucursal">Quien lo envía</Label>
                <Input
                  id="sucursal"
                  name="sucursal"
                  value={formData.sucursal}
                  onChange={handleChange}
                  placeholder="Ej: Juan Pérez (Opcional)"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="monto">Monto Depositado (Q) *</Label>
                <Input
                  id="monto"
                  name="monto"
                  type="number"
                  step="0.01"
                  value={formData.monto}
                  onChange={handleChange}
                  required
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tienda_id">Tienda / Centro de Costo</Label>
                <Select 
                  value={formData.tienda_id || "none"} 
                  onValueChange={(value) => setFormData(prev => ({...prev, tienda_id: value === "none" ? "" : value}))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar tienda (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin tienda</SelectItem>
                    {stores.map(store => (
                      <SelectItem key={store.id} value={store.id}>
                        {store.nombre} {store.codigo && `(${store.codigo})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="boleta_file">Imagen de la Boleta *</Label>
              <Input
                id="boleta_file"
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => setBoletaFile(e.target.files[0])}
                required
                className="cursor-pointer"
              />
              <p className="text-xs text-slate-500">
                Formatos aceptados: JPG, PNG, PDF (Máx. 10MB)
              </p>
            </div>

            {checkingDuplicate && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-sm text-yellow-800">🔍 Verificando duplicados...</p>
              </div>
            )}

            {duplicateWarning && duplicateWarning.is_duplicate && (
              <div className={`p-4 border rounded ${
                duplicateWarning.confidence === 'high' 
                  ? 'bg-red-50 border-red-300' 
                  : 'bg-yellow-50 border-yellow-300'
              }`}>
                <p className={`font-semibold mb-2 ${
                  duplicateWarning.confidence === 'high' ? 'text-red-800' : 'text-yellow-800'
                }`}>
                  {duplicateWarning.confidence === 'high' ? '🚫' : '⚠️'} {duplicateWarning.message}
                </p>
                <div className="text-sm text-slate-700 space-y-1">
                  <p><strong>Boleta encontrada:</strong> #{duplicateWarning.existing_boleta.numero_boleta}</p>
                  <p><strong>Banco:</strong> {duplicateWarning.existing_boleta.banco}</p>
                  <p><strong>Fecha:</strong> {duplicateWarning.existing_boleta.fecha}</p>
                  <p><strong>Monto:</strong> Q{duplicateWarning.existing_boleta.monto.toFixed(2)}</p>
                  {duplicateWarning.existing_boleta.created_by_name && (
                    <p><strong>Registrada por:</strong> {duplicateWarning.existing_boleta.created_by_name}</p>
                  )}
                </div>
                {duplicateWarning.confidence === 'high' && (
                  <div className="mt-3 p-2 bg-red-100 rounded text-xs text-red-900">
                    <strong>⛔ No se puede registrar esta boleta.</strong> Ya existe en el sistema. Si crees que es un error, contacta al administrador.
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/dashboard')}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading || (duplicateWarning?.is_duplicate && duplicateWarning?.confidence === 'high')}
                className={`text-white ${
                  duplicateWarning?.is_duplicate && duplicateWarning?.confidence === 'high'
                    ? 'bg-slate-400 cursor-not-allowed'
                    : 'bg-[#2c57c5] hover:bg-[#1e3a8a]'
                }`}
              >
                {loading ? 'Registrando...' : (
                  duplicateWarning?.is_duplicate && duplicateWarning?.confidence === 'high' ? (
                    <>
                      🚫 Boleta Duplicada
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Registrar Depósito
                    </>
                  )
                )}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
